import React, { Fragment, useState, useEffect } from 'react';
import { Control, Controller, FieldErrors, UseFormReturn } from 'react-hook-form';
import { FormData } from '../../account-create-form.types.ts';
import { UserType } from '@/types';
import { Checkbox, PhoneInput, FormInput, Modal } from '@/components';
import UserTypeSelector from '../user-type-selector';
import clsx from 'clsx';
import { useApi } from '@/api/useApi.tsx';
import { useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { CONTRACTS } from '@/constants';
import { LocalStorageKeys } from '@/constants/local-storage-keys.ts';
import { jsonTryParse } from '@/utility';
import { useLocation } from 'react-router';
import { isWebView, sendContractToWebView } from '@/utility/webview.ts';

interface FormStepProps {
	formState: FormData;
	handleFieldChange: (field: string, value: any) => void;
	form: {
		control: Control<FormData>;
		handleSubmit: UseFormReturn<FormData>['handleSubmit'];
		formState: { errors: FieldErrors<FormData> };
		setValue: UseFormReturn<FormData>['setValue'];
	};
	onSubmit: (data: FormData) => void;
	disabled?: boolean;
}

interface ContractResponse {
	id?: number;
	name: string;
	description: string;
	contractName: string;
	contractLink: string;
}

interface ContractData {
	id: number;
	name: string;
	description: string;
	contractName: string;
	contractLink: string;
}

interface StoredContract {
	contractName: string;
	id: number;
	accepted: boolean;
}

const STORAGE_KEY = LocalStorageKeys.REGISTER_CONTRACTS;

export const AccountCreateForm: React.FC<FormStepProps> = ({ formState, handleFieldChange, form, onSubmit, disabled }) => {
	const location = useLocation();
	const userTypeFromNavigation = location?.state?.accountType;
	const [activeContract, setActiveContract] = useState<string | null>(null);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [contractContent, setContractContent] = useState<ContractData | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [contractCache, setContractCache] = useState<Record<string, ContractData>>({});
	const [isWebViewEnvironment, setIsWebViewEnvironment] = useState(false);

	const { ContractControllerApiApplicant } = useApi();

	const {
		control,
		handleSubmit,
		formState: { errors },
		setValue
	} = form;

	useEffect(() => {
		setIsWebViewEnvironment(isWebView());

		const handleMessage = (event: MessageEvent) => {
			try {
				if (typeof event?.data === 'string') {
					const message = jsonTryParse(event?.data);

					if (message.type === 'contractApproved' && message.contractName) {
						handleContractApproved(message.contractName, message.contractId);
					}
				}
			} catch (e) {
				console.error('Message parse error:', e);
			}
		};

		window.addEventListener('message', handleMessage);

		return () => {
			window.removeEventListener('message', handleMessage);
		};
	}, []);

	const handleContractApproved = (contractName: string, contractId: number) => {
		if (contractName === CONTRACTS.COMMERCIAL_CONFIRM_TEXT) {
			setValue('acceptMarketing', true);
			handleFieldChange('acceptMarketing', true);
			saveContractToLocalStorage(contractName, contractId, true);
		} else if (contractName === CONTRACTS.APPLICANT_MEMBERSHIP_AGREEMENT) {
			setValue('acceptTerms', true);
			handleFieldChange('acceptTerms', true);
			saveContractToLocalStorage(contractName, contractId, true);
		} else if (contractName === CONTRACTS.CLARIFICATION_TEXT) {
			saveContractToLocalStorage(contractName, contractId, true);
		}
	};

	const mapResponseToContractData = (response: ContractResponse): ContractData => {
		return {
			id: response.id || 0,
			name: response.name,
			description: response.description,
			contractName: response.contractName,
			contractLink: response.contractLink
		};
	};

	const fetchAndSaveContract = async (contractName: string, accepted: boolean) => {
		try {
			if (contractCache[contractName]) {
				const contractId = contractCache[contractName].id;
				saveContractToLocalStorage(contractName, contractId, accepted);
				return;
			}

			const response = await ContractControllerApiApplicant.findContract(contractName);
			if (response.data && response.data.data) {
				const contractData = mapResponseToContractData(response.data.data);

				setContractCache(prev => ({
					...prev,
					[contractName]: contractData
				}));

				saveContractToLocalStorage(contractName, contractData.id, accepted);
			}
		} catch (error) {
			console.error('Error fetching contract:', error);
		}
	};

	const saveContractToLocalStorage = (contractName: string, contractId: number, accepted: boolean) => {
		try {
			const storedContractsJson = localStorage.getItem(STORAGE_KEY);
			let storedContracts: StoredContract[] = storedContractsJson ? jsonTryParse(storedContractsJson) : [];

			const existingContractIndex = storedContracts.findIndex(contract => contract.contractName === contractName);

			if (existingContractIndex !== -1) {
				if (accepted) {
					storedContracts[existingContractIndex].accepted = true;
					storedContracts[existingContractIndex].id = contractId;
				} else {
					storedContracts = storedContracts.filter(contract => contract.contractName !== contractName);
				}
			} else if (accepted) {
				storedContracts.push({
					contractName: contractName,
					id: contractId,
					accepted: true
				});
			}

			localStorage.setItem(STORAGE_KEY, JSON.stringify(storedContracts));
		} catch (error) {
			console.error('Error saving contract to localStorage:', error);
		}
	};

	const removeContractFromLocalStorage = (contractName: string) => {
		try {
			const storedContractsJson = localStorage.getItem(STORAGE_KEY);
			if (!storedContractsJson) return;

			let storedContracts: StoredContract[] = JSON.parse(storedContractsJson);

			storedContracts = storedContracts.filter(contract => contract.contractName !== contractName);

			localStorage.setItem(STORAGE_KEY, JSON.stringify(storedContracts));
		} catch (error) {
			console.error('Error removing contract from localStorage:', error);
		}
	};

	useEffect(() => {
		let scrollY: number | null = null;

		if (isModalOpen) {
			scrollY = window.scrollY;

			if (activeContract) {
				if (contractCache[activeContract]) {
					setContractContent(contractCache[activeContract]);
					setIsLoading(false);
				} else {
					setIsLoading(true);
				}
			}

			document.body.style.position = 'fixed';
			document.body.style.width = '100%';
			document.body.style.top = `-${scrollY}px`;
		} else {
			if (document.body.style.position === 'fixed') {
				document.body.style.position = '';
				document.body.style.width = '';
				document.body.style.top = '';

				if (scrollY !== null) {
					window.scrollTo(0, scrollY);
				}
			}
		}
	}, [isModalOpen, activeContract, contractCache]);

	useQueryProvider([QUERY_KEYS.findContractApplicant, activeContract], () => ContractControllerApiApplicant.findContract(activeContract), {
		enabled: !!activeContract && isModalOpen && !contractCache[activeContract],
		select: ({ data }) => data.data,
		refetchOnWindowFocus: false,
		onSuccess: data => {
			if (data) {
				const contractData = mapResponseToContractData(data);
				setContractContent(contractData);
				setContractCache(prev => ({
					...prev,
					[activeContract as string]: contractData
				}));
				setIsLoading(false);
			}
		},
		onError: () => {
			setIsLoading(false);
		}
	});

	const fetchContractDataForWebView = async (contractName: string) => {
		try {
			if (contractCache[contractName]) {
				const contract = contractCache[contractName];
				sendContractToWebView(contract, contractName !== CONTRACTS.CLARIFICATION_TEXT);
				return;
			}

			const response = await ContractControllerApiApplicant.findContract(contractName);
			if (response.data && response.data.data) {
				const contractData = mapResponseToContractData(response.data.data);

				setContractCache(prev => ({
					...prev,
					[contractName]: contractData
				}));

				sendContractToWebView(contractData, contractName !== CONTRACTS.CLARIFICATION_TEXT);
			}
		} catch (error) {
			console.error('Error fetching contract for WebView:', error);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Backspace' || e.key === 'Delete' || e.key === 'Tab' || e.key === 'Escape' || e.key === 'Enter' || e.key.includes('Arrow')) {
			return;
		}

		if (!/^[a-zA-ZğüşıöçĞÜŞİÖÇ\s]$/.test(e.key)) {
			e.preventDefault();
		}
	};

	const isFormValid = () => {
		const requiredFields = {
			firstName: formState.firstName,
			lastName: formState.lastName,
			phone: formState.phone,
			userType: formState.userType,
			acceptTerms: formState.acceptTerms
		};

		const hasAllRequiredFields = Object.values(requiredFields).every(value => (typeof value === 'boolean' ? value : Boolean(value)));

		return !disabled && hasAllRequiredFields && Object.keys(errors).length === 0 && formState.phone.length === 10;
	};

	const onContractChange = (e: React.MouseEvent<HTMLButtonElement>, contract: string) => {
		e.preventDefault();
		e.stopPropagation();

		if (isWebViewEnvironment) {
			fetchContractDataForWebView(contract);
		} else {
			setActiveContract(contract);
			setIsModalOpen(true);
		}
	};

	const closeModal = (e?: React.MouseEvent) => {
		if (e) {
			e.preventDefault();
			e.stopPropagation();
		}
		setIsModalOpen(false);
	};

	const handleAcceptAgreement = async (e?: React.MouseEvent) => {
		if (e) {
			e.preventDefault();
		}

		if (!contractContent) return;

		const contractId = contractContent.id;

		if (activeContract === CONTRACTS.COMMERCIAL_CONFIRM_TEXT) {
			setValue('acceptMarketing', true);
			handleFieldChange('acceptMarketing', true);
			saveContractToLocalStorage(CONTRACTS.COMMERCIAL_CONFIRM_TEXT, contractId, true);
		} else if (activeContract === CONTRACTS.APPLICANT_MEMBERSHIP_AGREEMENT) {
			setValue('acceptTerms', true);
			handleFieldChange('acceptTerms', true);
			saveContractToLocalStorage(CONTRACTS.APPLICANT_MEMBERSHIP_AGREEMENT, contractId, true);
		}

		closeModal();
	};

	const handleCheckboxChange = async (field: string, checked: boolean) => {
		const scrollPos = window.scrollY;

		let contractName = '';

		if (field === 'acceptTerms') {
			contractName = CONTRACTS.COMMERCIAL_CONFIRM_TEXT;
		} else if (field === 'acceptMarketing') {
			contractName = CONTRACTS.APPLICANT_MEMBERSHIP_AGREEMENT;
		}

		if (!contractName) return;

		if (checked) {
			await fetchAndSaveContract(contractName, true);
		} else {
			removeContractFromLocalStorage(contractName);
		}

		if (scrollPos > 0) {
			setTimeout(() => {
				window.scrollTo({
					top: scrollPos,
					behavior: 'instant'
				});
			}, 10);
		}
	};

	const handleFormSubmit = async (data: FormData, event?: React.FormEvent) => {
		if (event && typeof event.preventDefault === 'function') {
			event.preventDefault();
		}

		try {
			await onSubmit(data);
		} catch (error) {
			console.error('Form submission error:', error);
		}
	};

	return (
		<Fragment>
			<form
				onSubmit={e => {
					e.preventDefault();
					e.stopPropagation();
					handleSubmit(handleFormSubmit)(e);
				}}
				className="create-account-form">
				<div className="form-header">
					<h2>Hesap Oluştur</h2>
					<p>Uzmanlık alanınızı seçerek öğrencilerin akademik ve kişisel gelişimlerini desteklemek için hemen başvurun!</p>
				</div>

				<UserTypeSelector
					selectedType={formState.userType as UserType}
					onChange={type => {
						setValue('userType', type!);
						handleFieldChange('userType', type);
					}}
					accountType={userTypeFromNavigation}
				/>
				{errors.userType && <span className="error-message">{errors.userType.message}</span>}

				<div className="form-group-container">
					<h3>Ad Soyad</h3>
					<div className="form-row">
						<div>
							<Controller
								name="firstName"
								control={control}
								render={({ field }) => (
									<FormInput
										placeholder="Ad"
										{...field}
										maxLength={30}
										error={!!errors?.firstName}
										onKeyDown={handleKeyDown}
										onChange={e => {
											field.onChange(e);
											handleFieldChange('firstName', e.target.value);
										}}
									/>
								)}
							/>
							{errors.firstName && (
								<span className="error-message">
									<i className="icons-info-circle" />
									{errors.firstName.message}
								</span>
							)}
						</div>

						<div>
							<Controller
								name="lastName"
								control={control}
								render={({ field }) => (
									<FormInput
										placeholder="Soyad"
										{...field}
										error={!!errors?.lastName}
										maxLength={30}
										onKeyDown={handleKeyDown}
										onChange={e => {
											field.onChange(e);
											handleFieldChange('lastName', e.target.value);
										}}
									/>
								)}
							/>
							{errors.lastName && (
								<span className="error-message">
									<i className="icons-info-circle" />
									{errors.lastName.message}
								</span>
							)}
						</div>
					</div>
				</div>

				<div>
					<Controller
						name="phone"
						control={control}
						render={({ field }) => (
							<PhoneInput
								value={field.value}
								onChange={value => {
									field.onChange(value);
									handleFieldChange('phone', value);
								}}
							/>
						)}
					/>
					{errors.phone && (
						<span className="error-message">
							<i className="icons-info-circle" />
							{errors.phone.message}
						</span>
					)}
				</div>

				<div className="form-checkboxes">
					<div className="clarification-wrapper">
						<i className="icons-info-vector" />
						<p>
							Kişisel verileriniz{' '}
							<button
								className="button"
								type="button"
								onClick={e => {
									e.preventDefault();
									e.stopPropagation();
									onContractChange(e, CONTRACTS.CLARIFICATION_TEXT);
								}}>
								Aydınlatma Metni
							</button>{' '}
							kapsamında işlenmektedir.
						</p>
					</div>
					<div>
						<Controller
							name="acceptTerms"
							control={control}
							render={({ field: { onChange, value } }) => (
								<Checkbox
									checked={value}
									className={clsx(errors.acceptTerms && 'error')}
									onChange={e => {
										const scrollPos = window.scrollY;
										const isChecked = e.target.checked;
										onChange(isChecked);
										handleFieldChange('acceptTerms', isChecked);
										handleCheckboxChange('acceptTerms', isChecked);

										if (scrollPos > 0) {
											setTimeout(() => {
												window.scrollTo({
													top: scrollPos,
													behavior: 'instant'
												});
											}, 10);
										}
									}}
									label={
										<span className="checkbox-text">
											<button
												className="button"
												type="button"
												onClick={e => {
													e.preventDefault();
													e.stopPropagation();
													const scrollPos = window.scrollY;
													onContractChange(e, CONTRACTS.APPLICANT_MEMBERSHIP_AGREEMENT);

													if (scrollPos > 0) {
														setTimeout(() => {
															window.scrollTo({
																top: scrollPos,
																behavior: 'instant'
															});
														}, 10);
													}
												}}>
												Bire Bir Üyelik Koşulları
											</button>{' '}
											'nı okudum ve kabul ediyorum.
										</span>
									}
								/>
							)}
						/>
					</div>

					<Controller
						name="acceptMarketing"
						control={control}
						render={({ field: { onChange, value } }) => (
							<Checkbox
								checked={value}
								onChange={e => {
									const scrollPos = window.scrollY;
									const isChecked = e.target.checked;
									onChange(isChecked);
									handleFieldChange('acceptMarketing', isChecked);
									handleCheckboxChange('acceptMarketing', isChecked);

									if (scrollPos > 0) {
										setTimeout(() => {
											window.scrollTo({
												top: scrollPos,
												behavior: 'instant'
											});
										}, 10);
									}
								}}
								label={
									<span className="checkbox-text">
										<button
											className="button"
											type="button"
											onClick={e => {
												e.preventDefault();
												e.stopPropagation();
												const scrollPos = window.scrollY;
												onContractChange(e, CONTRACTS.COMMERCIAL_CONFIRM_TEXT);

												if (scrollPos > 0) {
													setTimeout(() => {
														window.scrollTo({
															top: scrollPos,
															behavior: 'instant'
														});
													}, 10);
												}
											}}>
											Tanıtımlardan
										</button>{' '}
										haberdar olmak istiyorum.
									</span>
								}
							/>
						)}
					/>
				</div>

				<button
					type="submit"
					className="submit-button"
					disabled={!isFormValid()}
					onClick={() => {
						const scrollPos = window.scrollY;
						if (scrollPos > 0) {
							setTimeout(() => {
								window.scrollTo({
									top: scrollPos,
									behavior: 'instant'
								});
							}, 10);
						}
					}}>
					Hemen Başvur
				</button>

				<div className="sign-in">
					<p>Zaten bir hesabın mı var?</p>
					<a
						href="/login"
						className="link">
						Giriş Yap
					</a>
				</div>
			</form>

			{!isWebViewEnvironment && (
				<Modal
					onClose={e => closeModal(e as React.MouseEvent)}
					size="lg"
					contentClassName="contract-modal-container"
					closeOnClickOutside
					variant="centered"
					isOpen={isModalOpen}>
					{isLoading ? (
						<div className="loading-contract">Sözleşme yükleniyor...</div>
					) : contractContent ? (
						<div className="contract-modal-content">
							{contractContent.contractLink && (
								<>
									<div
										className={clsx('contract-iframe-container', {
											'full-container': activeContract === CONTRACTS.CLARIFICATION_TEXT
										})}>
										<iframe
											src={contractContent.contractLink}
											title={contractContent.name}
										/>
									</div>
									{activeContract !== CONTRACTS.CLARIFICATION_TEXT && (
										<div className="agreement-button-wrapper">
											<button
												onClick={e => handleAcceptAgreement(e)}
												className="agreement-button">
												Okudum, kabul ediyorum
											</button>
										</div>
									)}
								</>
							)}
						</div>
					) : (
						<div className="loading-contract">Sözleşme bulunamadı</div>
					)}
				</Modal>
			)}
		</Fragment>
	);
};
